"use client";

import React, { useState } from "react";
import { Copy, CircleCheck, Send, Sparkles, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const handleCustomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit(e);
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-accent" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border/50 hover:bg-accent/10 text-primary">
          Send Another Message
        </Button>
      </div>;
  }
  return <>
      <Form onSubmit={handleCustomSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input className="peer pt-6 pb-2 h-14 bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/10 transition-all rounded-md" placeholder=" " {...field} />
              </FormControl>
              <FormLabel className="absolute left-3 top-4 text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-1.5 peer-focus:text-xs pointer-events-none">
                Full Name
              </FormLabel>
              <FormMessage className="pt-1 text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input type="email" className="peer pt-6 pb-2 h-14 bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/10 transition-all rounded-md" placeholder=" " {...field} />
              </FormControl>
              <FormLabel className="absolute left-3 top-4 text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-1.5 peer-focus:text-xs pointer-events-none">
                Corporate Email
              </FormLabel>
              <FormMessage className="pt-1 text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="industry" rules={{
        required: "Industry is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
            <FormControl>
              <Input className="peer pt-6 pb-2 h-14 bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/10 transition-all rounded-md" placeholder=" " {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-1.5 peer-focus:text-xs pointer-events-none">
              Industry (e.g. Pharma, Cosmetics)
            </FormLabel>
            <FormMessage className="pt-1 text-xs" />
          </FormItem>} />

      <FormField control={control} name="project_details" rules={{
        required: "Please provide some project details"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
            <FormControl>
              <Textarea className="peer pt-6 pb-2 min-h-[140px] resize-none bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/10 transition-all rounded-md" placeholder=" " {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-1.5 peer-focus:text-xs pointer-events-none">
              Material Interest & Goals
            </FormLabel>
            <FormMessage className="pt-1 text-xs" />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm bg-destructive/10 border border-destructive/20 text-destructive rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-default font-bold bg-accent hover:bg-accent/90 text-accent-foreground transition-all border-0 shadow-md rounded-lg">
        {isSubmitting ? <span className="flex items-center gap-2">
            <Sparkles className="size-5 animate-pulse" />
            Processing...
          </span> : <span className="flex items-center gap-2">
            Book Your Free Call
            <Send className="size-4 ml-1" />
          </span>}
      </Button>
    </Form>
    </>;
}
function DirectContact() {
  const [copied, setCopied] = useState(false);
  const email = "info@jcsourcingpartners.com";
  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="flex flex-col justify-center h-full space-y-8 p-8 lg:p-12 bg-primary/5 rounded-lg border border-border/50 backdrop-blur-sm relative overflow-hidden group">
      {}
      <div className="absolute -top-24 -right-24 size-48 bg-primary/5 rounded-lg blur-3xl group-hover:bg-primary/10 transition-colors duration-700" />
      <div className="absolute -bottom-24 -left-24 size-48 bg-accent/5 rounded-lg blur-3xl group-hover:bg-accent/10 transition-colors duration-700" />
      
      <div className="relative z-10 space-y-4">
        <h3 className="text-2xl md:text-3xl font-default font-semibold text-primary">
          Direct Expert Access
        </h3>
        <p className="text-muted-foreground text-lg font-default">Skip the form and connect with a Senior Sourcing Partner directly. We respond within one business day.</p>
      </div>

      <div className="relative z-10">
        <Button onClick={handleCopy} variant="outline" className="w-full flex items-center justify-between p-4 md:p-6 bg-background hover:bg-muted border border-border rounded-lg transition-all duration-300 h-auto shadow-sm">
          <div className="flex items-center gap-4 overflow-hidden">
            <span className="text-2xl md:text-3xl shrink-0"></span>
            <span className="font-default font-medium text-primary truncate !text-xl">
              {email}
            </span>
          </div>
          <div className="shrink-0 ml-4 size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
            {copied ? <CircleCheck className="size-5" /> : <Copy className="size-5" />}
          </div>
        </Button>
        
        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground font-default">
          <ArrowRight className="size-4 text-accent" />
          <span>Click to copy address</span>
        </div>
      </div>
    </div>;
}
export default function LetsBuildTogether() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="247786" id="lets-talk" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight mb-6 text-primary">
            Ready to Increase Your <span className="text-accent">Profit Margins?</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Leverage 20+ years of Fortune 500 expertise. Book your free cost-reduction call today and discover AI-enhanced sourcing insights.
          </p>
        </div>

        <div ref={contentRef} className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 transition-all duration-1000 delay-200 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-3 bg-card backdrop-blur-xl border border-border rounded-lg p-6 md:p-10 shadow-md">
            <div className="mb-8">
              <h3 className="text-2xl font-default font-semibold mb-2 text-primary">Consultation Request</h3>
              <p className="text-muted-foreground font-default">Tell us about your packaging goals and current challenges.</p>
            </div>
            
            <FormProvider formKey="lets_talk_form_home" formId="lets_talk_form_home" sectionName="lets-talk" defaultValues={{
            name: "",
            email: "",
            industry: "",
            project_details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            industry: {
              required: true
            },
            project_details: {
              required: true
            }
          }} submitText="Book Your Free Call" successMessage="Thank you for your interest. A senior sourcing expert will review your details and contact you within 24 hours.">
              <ContactForm />
            </FormProvider>
          </div>

          {}
          <div className="lg:col-span-2">
            <DirectContact />
          </div>
        </div>
      </div>
    </section>;
}